/**
 * Wachtwoord vergeten: aanvragen en opnieuw instellen.
 *
 * Eén bestand, twee schermen, want ze zijn twee helften van dezelfde handeling en de
 * gebruiker doorloopt ze achter elkaar — met een mail ertussen.
 *
 * ## Wat het scherm bij de aanvraag zegt, en waarom
 *
 * De API antwoordt altijd hetzelfde, of het adres nu bestaat of niet: anders is dit
 * formulier een manier om te ontdekken wie er klant is bij dit platform (§17.47). Dat
 * kost iets echts — wie zich vertypt in zijn eigen adres, blijft wachten op een mail
 * die nooit komt. Het scherm zegt daarom uitdrukkelijk dát er niets komt als het adres
 * onbekend is, in plaats van "gelukt" te melden en de gebruiker in het ongewisse te
 * laten.
 *
 * ## Het token staat in de fragmentidentifier
 *
 * `#token` en niet `?token`, dezelfde afweging als bij de sessieoverdracht (§17.43):
 * alles achter het hekje bereikt geen server, dus het staat niet in toegangslogboeken
 * en niet in de `Referer` van een volgende aanvraag. Het wordt na het lezen meteen uit
 * de adresbalk gehaald.
 */
import { useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, useNavigate } from 'react-router'

import {
  authErrorMessageKey,
  completePasswordReset,
  requestPasswordReset,
} from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'

/** Moet gelijk blijven aan `passwordField` in de API. */
const MIN_LENGTH = 12

export function ForgotPasswordPage(): ReactElement {
  const t = useTranslate()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const result = await requestPasswordReset(email.trim())
    setBusy(false)

    if (!result.ok) {
      // Alleen een echte storing komt hier terecht; een onbekend adres niet.
      setError(t(authErrorMessageKey(result.error, 'login')))
      return
    }
    setSent(true)
  }

  return (
    <section className="auth">
      <h1>{t('dashboard.forgot.title')}</h1>

      {sent ? (
        <>
          <p className="account__done" role="status">
            {t('dashboard.forgot.sent')}
          </p>
          {/* De eerlijke zin: er komt niets als het adres onbekend is. */}
          <p className="account__hint">{t('dashboard.forgot.sentHint')}</p>
          <p>
            <Link to="/login">{t('dashboard.forgot.backToLogin')}</Link>
          </p>
        </>
      ) : (
        <>
          <p className="account__hint">{t('dashboard.forgot.intro')}</p>
          {error !== null ? (
            <p className="account__error" role="alert">
              {error}
            </p>
          ) : null}

          <form
            className="auth-form"
            onSubmit={(event) => {
              void submit(event)
            }}
          >
            <label>
              <span>{t('dashboard.auth.login.email')}</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                required
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
              />
            </label>
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? t('dashboard.forgot.sending') : t('dashboard.forgot.submit')}
            </button>
          </form>

          <p>
            <Link to="/login">{t('dashboard.forgot.backToLogin')}</Link>
          </p>
        </>
      )}
    </section>
  )
}

export function ResetPasswordPage(): ReactElement {
  const t = useTranslate()
  const navigate = useNavigate()
  const [token, setToken] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const readRef = useRef(false)

  useEffect(() => {
    if (readRef.current) {
      return
    }
    readRef.current = true
    const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ''))
    // Het geheim uit de adresbalk halen zodra het gelezen is.
    window.history.replaceState(null, '', window.location.pathname)
    setToken(fromHash)
  }, [])

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)

    if (next !== repeat) {
      setError(t('dashboard.account.mismatch'))
      return
    }

    setBusy(true)
    const result = await completePasswordReset(token, next)
    setBusy(false)

    if (!result.ok) {
      setError(t(authErrorMessageKey(result.error, 'reset')))
      return
    }
    setDone(true)
  }

  if (done) {
    return (
      <section className="auth">
        <h1>{t('dashboard.reset.title')}</h1>
        <p className="account__done" role="status">
          {t('dashboard.reset.done')}
        </p>
        <button
          className="button button--primary"
          type="button"
          onClick={() => {
            void navigate('/login', { replace: true })
          }}
        >
          {t('dashboard.reset.toLogin')}
        </button>
      </section>
    )
  }

  return (
    <section className="auth">
      <h1>{t('dashboard.reset.title')}</h1>

      {token === '' ? (
        <>
          {/* Zonder token is dit scherm zinloos; dat zeggen is bruikbaarder dan een
              leeg formulier tonen dat bij het versturen faalt. */}
          <p className="account__error" role="alert">
            {t('dashboard.reset.noToken')}
          </p>
          <p>
            <Link to="/forgot">{t('dashboard.reset.requestNew')}</Link>
          </p>
        </>
      ) : (
        <>
          {error !== null ? (
            <p className="account__error" role="alert">
              {error}
            </p>
          ) : null}

          <form
            className="auth-form"
            onSubmit={(event) => {
              void submit(event)
            }}
          >
            <label>
              <span>{t('dashboard.account.new', { min: MIN_LENGTH })}</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                value={next}
                required
                autoFocus
                onChange={(event) => {
                  setNext(event.target.value)
                }}
              />
            </label>
            <label>
              <span>{t('dashboard.account.repeat')}</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                value={repeat}
                required
                onChange={(event) => {
                  setRepeat(event.target.value)
                }}
              />
            </label>

            <p className="account__hint">{t('dashboard.reset.hint')}</p>

            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? t('dashboard.reset.saving') : t('dashboard.reset.submit')}
            </button>
          </form>
        </>
      )}
    </section>
  )
}
